// Colunas da tabela da Consulta — AGRUPADA por envio: uma linha por
// disparo (data/evento/ORIGEM) e uma coluna "Canais" com uma sub-linha
// por canal (canal + status + destinatário) que linka para a PÁGINA de
// detalhe DAQUELE canal. Ícones semânticos (canal, status, origem) no
// padrão discreto Stripe/Vercel: 12–14px, cor só onde carrega sentido.
import type { ColumnDef } from "@tanstack/react-table"
import type { TFunction } from "i18next"
import {
  BanIcon,
  CalendarIcon,
  CheckCheckIcon,
  CheckCircle2Icon,
  ClockIcon,
  ExternalLinkIcon,
  type LucideIcon,
  MailIcon,
  MessageCircleIcon,
  MessageSquareIcon,
  PackageIcon,
  RepeatIcon,
  SendIcon,
  XCircleIcon,
} from "lucide-react"
import { Link } from "react-router-dom"

import { Badge } from "@/components/ui/badge"
import { buttonVariants } from "@/components/ui/button"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import type {
  NotificationDispatchType,
  NotificationMacroStatusType,
  NotificationOriginRefType,
  NotificationOriginTransactionType,
  NotificationSearchChannelType,
} from "@/types/notification-center"
import { DateHelper } from "@/utils"

import { NOTIFICATION_MACRO_STATUS_BADGE_CLASSES } from "../constants"

const columnsPrefix =
  "pages.notificationCenter.searchPage.columns"

// Ícone por canal (fallback e-mail). Reutilizado no detalhe.
const CHANNEL_ICONS: Record<string, LucideIcon> = {
  EMAIL: MailIcon,
  WHATSAPP: MessageCircleIcon,
  SMS: MessageSquareIcon,
}

// Ícone semântico por macro-status (mesma leitura das cores do badge).
const MACRO_STATUS_ICONS: Record<
  NotificationMacroStatusType,
  LucideIcon
> = {
  QUEUED: ClockIcon,
  SENT: SendIcon,
  DELIVERED: CheckCircle2Icon,
  READ: CheckCheckIcon,
  FAILED: XCircleIcon,
  CANCELED: BanIcon,
}

// Ícone por tipo de origem (pedido/assinatura).
const ORIGIN_ICONS: Record<
  NotificationOriginTransactionType,
  LucideIcon
> = {
  ORDER: PackageIcon,
  SUBSCRIPTION: RepeatIcon,
}

// Badge de canal com ícone — reutilizado na Consulta e no detalhe.
export const NotificationChannelBadge = ({
  channel,
}: {
  channel: NotificationSearchChannelType
}) => {
  const Icon = CHANNEL_ICONS[channel] ?? MailIcon
  return (
    <Badge
      variant="outline"
      className="gap-1 rounded-full font-medium"
    >
      <Icon size={12} aria-hidden="true" />
      {channel}
    </Badge>
  )
}

// Badge soft do macro-status (cor semântica + ícone).
export const NotificationMacroStatusBadge = ({
  macroStatus,
  label,
}: {
  macroStatus: NotificationMacroStatusType
  label: string
}) => {
  const Icon = MACRO_STATUS_ICONS[macroStatus]
  return (
    <Badge
      variant="outline"
      className={cn(
        "gap-1 rounded-full border font-medium",
        NOTIFICATION_MACRO_STATUS_BADGE_CLASSES[macroStatus]
      )}
    >
      {Icon && <Icon size={12} aria-hidden="true" />}
      {label}
    </Badge>
  )
}

// Rota do portal para a origem do envio (pedido/assinatura).
export const getOriginHref = (
  origin: NotificationOriginRefType
) =>
  origin.type === "ORDER"
    ? `/orders/details/${origin.transactionId}`
    : `/subscriptions/details/${origin.transactionId}`

export const getNotificationSearchColumns = (
  t: TFunction,
  // Monta o href da página de detalhe do ENVIO (schedulerId, com ?from=).
  getDetailHref: (schedulerId: string) => string
): ColumnDef<NotificationDispatchType>[] => [
  {
    header: t(`${columnsPrefix}.date`),
    accessorKey: "notificationDate",
    cell: ({ row }) => (
      <span className="flex items-center gap-1.5 whitespace-nowrap text-sm tabular-nums">
        <CalendarIcon
          size={14}
          aria-hidden="true"
          className="text-muted-foreground"
        />
        {DateHelper.format(
          row.original.notificationDate,
          "dd/MM/yyyy HH:mm:ss"
        )}
      </span>
    ),
  },
  {
    header: t(`${columnsPrefix}.event`),
    accessorKey: "eventLabel",
    cell: ({ row }) => (
      <div className="flex flex-col gap-0.5">
        <span className="text-sm">
          {row.original.eventLabel}
        </span>
        {row.original.scheduled && (
          <span className="text-xs text-muted-foreground">
            {t(
              `pages.notificationCenter.searchPage.scheduledTag`
            )}
          </span>
        )}
      </div>
    ),
  },
  {
    // Origem ANTES dos canais.
    header: t(`${columnsPrefix}.origin`),
    accessorKey: "origin",
    cell: ({ row }) => {
      const OriginIcon =
        ORIGIN_ICONS[row.original.origin.type]
      return (
        <Badge
          variant="outline"
          className="gap-1 rounded-full border font-medium hover:bg-accent"
        >
          <OriginIcon size={12} aria-hidden="true" />
          <Link
            to={getOriginHref(row.original.origin)}
            aria-label={t(
              `pages.notificationCenter.searchPage.originLinkLabel.${row.original.origin.type}`
            )}
          >
            {t(
              `pages.notificationCenter.searchPage.filters.originGroup.${row.original.origin.type}`
            )}
          </Link>
        </Badge>
      )
    },
  },
  {
    header: t(`${columnsPrefix}.channels`),
    accessorKey: "channels",
    // Canais do envio como CONTEÚDO (badge + status + destinatário), um por
    // linha. Não é link: abrir o detalhe é ação única na coluna "Ações"
    // (o detalhe é do ENVIO/scheduler, não de um canal).
    cell: ({ row }) => (
      <div className="flex flex-col gap-1">
        {row.original.channels.map((channel) => (
          <span
            key={channel.id}
            className="flex items-center gap-2"
          >
            <NotificationChannelBadge
              channel={channel.channel}
            />
            <NotificationMacroStatusBadge
              macroStatus={channel.macroStatus}
              label={t(
                `pages.notificationCenter.searchPage.macroStatus.${channel.macroStatus}`
              )}
            />
            <span className="text-xs text-muted-foreground">
              {channel.recipient}
            </span>
          </span>
        ))}
      </div>
    ),
  },
  {
    // Ações: UM botão-ícone "Ver detalhes" por envio (padrão da listagem de
    // Orders) — abre a página de detalhe do ENVIO (schedulerId).
    header: t(`${columnsPrefix}.actions`),
    id: "actions",
    cell: ({ row }) => (
      <div className="flex items-center">
        <Tooltip>
          <TooltipTrigger asChild>
            <Link
              to={getDetailHref(row.original.schedulerId)}
              className={cn(
                buttonVariants({
                  size: "icon",
                  variant: "ghost",
                }),
                "h-8 w-8"
              )}
            >
              <ExternalLinkIcon size={16} />
              <span className="sr-only">
                {t("common.viewDetails")}
              </span>
            </Link>
          </TooltipTrigger>
          <TooltipContent>
            {t("common.viewDetails")}
          </TooltipContent>
        </Tooltip>
      </div>
    ),
  },
]
